import React, { useState, useMemo } from 'react';
import { TextField, Box, Typography, Dialog, Button, IconButton, InputAdornment } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as SuccessTick } from '../../../assets/icons/SuccessTick.svg';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import { ReactComponent as CetraLogo } from '../../../assets/icons/CetraLogo.svg';
import { ReactComponent as LogoCircle } from '../../../assets/icons/LogoCircle.svg';
import { ReactComponent as EyeOffIcon } from '../../../assets/icons/EyeOff.svg';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import PinkGradientButton from '../../UIControls/PinkGradientButton';
import { loginStyles } from './styles/onboard-login.styles';
import { registerStyles } from './styles/onboard-register.styles';

const inputSx = loginStyles.input;

const passwordRequirements = [
    { test: (pwd) => pwd.length >= 8, text: 'Use at least 8 characters' },
    {
        test: (pwd) => /[A-Z]/.test(pwd) && /[a-z]/.test(pwd),
        text: 'Use upper and lower case letters (e.g. Aa)',
        mobileText: 'Use upper and lower case letters',
    },
    { test: (pwd) => /\d/.test(pwd), text: 'Use a number (e.g. 1234)' },
    { test: (pwd) => /[!@#$%^&*()_+\-=[\]{}|;':",.<>?]/.test(pwd), text: 'Use a symbol (e.g. !@#$)' },
];

const OnboardLoginResetPasswordScreen = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetStage, setResetStage] = useState('request'); // 'request' | 'verify' | 'reset'
    const [isSuccessOpen, setIsSuccessOpen] = useState(false);
    const [isErrorOpen, setIsErrorOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const passwordCriteria = useMemo(
        () => passwordRequirements.map((req) => ({ ...req, meets: req.test(newPassword) })),
        [newPassword],
    );
    const isPasswordValid = useMemo(() => passwordCriteria.every((c) => c.meets), [passwordCriteria]);
    const canSubmit = useMemo(
        () => isPasswordValid && newPassword === confirmPassword,
        [isPasswordValid, newPassword, confirmPassword],
    );

    const hasStartedTypingPassword = newPassword.length > 0;

    const getValidationDotStyle = (criterion) => {
        if (!hasStartedTypingPassword) return registerStyles.validationDotDefault;
        return criterion.meets ? registerStyles.validationDotSuccess : registerStyles.validationDotError;
    };

    const renderPasswordVisibilityAdornment = () => (
        <InputAdornment position="end">
            <IconButton
                onClick={() => setShowPassword((prev) => !prev)}
                edge="end"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disableRipple
            >
                <EyeOffIcon style={{ width: 19, height: 16, opacity: showPassword ? 0.5 : 1 }} />
            </IconButton>
        </InputAdornment>
    );

    const showError = (msg) => {
        setErrorMessage(msg);
        setIsErrorOpen(true);
    };

    const handleForgotPassword = async () => {
        if (!email) return;
        try {
            await resetPassword({ username: email.toLowerCase() });
            setResetStage('verify');
        } catch (error) {
            showError(
                error.code === 'UserNotFoundException'
                    ? 'No account found with the provided email address.'
                    : 'Failed to send verification code.',
            );
        }
    };

    const handleVerifyCode = () => {
        if (!verificationCode) return;
        setResetStage('reset');
    };

    const handleResetPassword = async () => {
        if (!canSubmit) return;
        try {
            await confirmResetPassword({
                username: email.toLowerCase(),
                confirmationCode: verificationCode,
                newPassword,
            });
            setIsSuccessOpen(true);
        } catch (error) {
            if (error.code === 'CodeMismatchException') {
                showError('The verification code entered is incorrect. Please try again.');
                setResetStage('verify');
            } else if (error.code === 'ExpiredCodeException') {
                showError('The verification code has expired. Please request a new one.');
                setResetStage('request');
            } else if (error.code === 'InvalidPasswordException') {
                showError('Password does not meet requirements.');
            } else {
                showError('Failed to reset password.');
                setResetStage('verify');
            }
        }
    };

    const handleGoBack = () => {
        if (resetStage === 'request') navigate(-1);
        else if (resetStage === 'verify') setResetStage('request');
        else setResetStage('verify');
    };

    const handleKeyDown = (e, action) => {
        if (e.key === 'Enter') action();
    };

    const subtitle = {
        request: 'We will send a verification code to your email',
        verify: 'Please enter the verification code sent to your email',
        reset: 'Please enter your new password',
    }[resetStage];

    return (
        <Box sx={loginStyles.pageContainer}>
            {/* Logo */}
            <Box sx={loginStyles.logo}>
                <Box sx={{ position: 'relative', ...loginStyles.logoSize }}>
                    <Box component={LogoCircle} sx={loginStyles.logoCircle} />
                    <Box component={CetraLogo} sx={loginStyles.logoIcon} />
                </Box>
            </Box>

            {/* Card */}
            <Box sx={loginStyles.card}>
                <Typography sx={loginStyles.title}>Reset your password</Typography>

                <Typography
                    sx={{
                        fontSize: { xs: '12px', md: '16px' },
                        lineHeight: { xs: '12px', md: 'normal' },
                        color: '#616063',
                        textAlign: 'center',
                        letterSpacing: { xs: '-0.24px', md: '-0.32px' },
                        mb: { xs: 1.5, md: 2 },
                    }}
                >
                    {resetStage === 'verify' ? (
                        <>
                            <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                Please enter the verification code sent to your email
                            </Box>
                            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                Please enter the verification code
                            </Box>
                        </>
                    ) : (
                        subtitle
                    )}
                </Typography>

                {/* Step 1 — Email */}
                {resetStage === 'request' && (
                    <>
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={loginStyles.label}>Email</Typography>
                            <TextField
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, handleForgotPassword)}
                                fullWidth
                                sx={inputSx}
                            />
                        </Box>
                        <PinkGradientButton
                            onClick={handleForgotPassword}
                            disabled={!email}
                            sx={{
                                width: '100%',
                                mt: 1,
                                height: { xs: '35.434px', md: 64 },
                                borderRadius: { xs: '22.146px', md: '36px' },
                                fontSize: { xs: '16px', md: '22px' },
                                fontWeight: 500,
                                letterSpacing: { xs: '-0.32px', md: '-0.44px' },
                            }}
                        >
                            Send code
                        </PinkGradientButton>
                    </>
                )}

                {/* Step 2 — Verify code */}
                {resetStage === 'verify' && (
                    <>
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={loginStyles.label}>Verification code</Typography>
                            <TextField
                                type="text"
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, handleVerifyCode)}
                                fullWidth
                                sx={inputSx}
                            />
                        </Box>
                        <PinkGradientButton
                            onClick={handleVerifyCode}
                            disabled={!verificationCode}
                            sx={{
                                width: '100%',
                                mt: 1,
                                height: { xs: '35.434px', md: 64 },
                                borderRadius: { xs: '22.146px', md: '36px' },
                                fontSize: { xs: '16px', md: '22px' },
                                fontWeight: 500,
                                letterSpacing: { xs: '-0.32px', md: '-0.44px' },
                            }}
                        >
                            Verify code
                        </PinkGradientButton>
                    </>
                )}

                {/* Step 3 — New password */}
                {resetStage === 'reset' && (
                    <>
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={loginStyles.label}>New password</Typography>
                            <TextField
                                type={showPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                fullWidth
                                sx={inputSx}
                                inputProps={{ className: 'mp-mask-password' }}
                                InputProps={{
                                    endAdornment: renderPasswordVisibilityAdornment(),
                                }}
                            />
                        </Box>
                        <Box sx={{ mb: 1 }}>
                            <Typography sx={loginStyles.label}>Confirm password</Typography>
                            <TextField
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, handleResetPassword)}
                                fullWidth
                                sx={inputSx}
                                error={confirmPassword.length > 0 && newPassword !== confirmPassword}
                                helperText={
                                    confirmPassword.length > 0 && newPassword !== confirmPassword
                                        ? 'Passwords do not match.'
                                        : ''
                                }
                                inputProps={{ className: 'mp-mask-password' }}
                                InputProps={{
                                    endAdornment: renderPasswordVisibilityAdornment(),
                                }}
                            />
                        </Box>

                        {/* Requirements — same dot system as Create account screen */}
                        <Box sx={registerStyles.validationContainer}>
                            {passwordCriteria.map((criterion, idx) => (
                                <Box key={idx} sx={registerStyles.validationItem}>
                                    <Box
                                        sx={{
                                            ...registerStyles.validationDot,
                                            ...getValidationDotStyle(criterion),
                                        }}
                                    />
                                    <Typography sx={registerStyles.validationText}>
                                        {criterion.mobileText ? (
                                            <>
                                                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                                                    {criterion.text}
                                                </Box>
                                                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                                                    {criterion.mobileText}
                                                </Box>
                                            </>
                                        ) : (
                                            criterion.text
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        <PinkGradientButton
                            onClick={handleResetPassword}
                            disabled={!canSubmit}
                            sx={{
                                width: '100%',
                                mt: 0.5,
                                height: { xs: '35.434px', md: 64 },
                                borderRadius: { xs: '22.146px', md: '36px' },
                                fontSize: { xs: '16px', md: '22px' },
                                fontWeight: 500,
                                letterSpacing: { xs: '-0.32px', md: '-0.44px' },
                            }}
                        >
                            Reset password
                        </PinkGradientButton>
                    </>
                )}
            </Box>

            {/* Go back — left-aligned with card */}
            <Box sx={{ width: '100%', maxWidth: 646, mt: 2, px: { xs: 0, md: 0 } }}>
                <Button
                    onClick={handleGoBack}
                    sx={{
                        color: '#FF669B',
                        textDecoration: 'underline',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '14px', md: '22px' },
                        letterSpacing: { xs: '-0.28px', md: '-0.44px' },
                        lineHeight: { xs: '14px', md: '22px' },
                        p: 0,
                        minWidth: 'auto',
                        '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
                    }}
                >
                    ← Go back
                </Button>
            </Box>

            {/* Success dialog */}
            <Dialog
                open={isSuccessOpen}
                PaperProps={{
                    sx: {
                        borderRadius: { xs: '13.425px', md: '28px' },
                        width: { xs: 358, md: 750 },
                        maxWidth: 750,
                        px: { xs: 2.5, md: 8 },
                        pt: { xs: 3, md: 5 },
                        pb: { xs: 3, md: 5 },
                        textAlign: 'center',
                    },
                }}
            >
                {/* Pink outline circle icon — inside card, centered at top */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 1.5, md: 2 } }}>
                    <Box component={SuccessTick} sx={{ width: { xs: 52, md: 104 }, height: { xs: 52, md: 104 } }} />
                </Box>

                <Typography
                    sx={{
                        fontSize: { xs: '22px', md: '36px' },
                        fontWeight: 600,
                        color: '#000453',
                        letterSpacing: { xs: '-0.88px', md: '-1.44px' },
                        lineHeight: { xs: '22px', md: '36px' },
                        mb: { xs: '8px', md: '8px' },
                    }}
                >
                    Password reset successfully
                </Typography>
                <Typography
                    sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: { xs: '10px', md: '20px' },
                        fontWeight: 400,
                        lineHeight: { xs: '10px', md: '20px' },
                        color: '#616063',
                        letterSpacing: { xs: '-0.4px', md: '-0.8px' },
                        mb: { xs: '21px', md: '42.01px' },
                    }}
                >
                    You can now log in using your new password
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <PinkGradientButton
                        onClick={() => navigate(`/onboard/login${window.location.search || ''}`)}
                        sx={{
                            width: { xs: '99px', md: '197.732px' },
                            height: { xs: '28px', md: '52.267px' },
                            fontSize: { xs: '14px', md: '16px' },
                            fontWeight: 500,
                            letterSpacing: { xs: '-0.56px', md: '-0.32px' },
                            borderRadius: { xs: '13.425px', md: '28px' },
                            marginY: 0,
                        }}
                    >
                        Log in
                    </PinkGradientButton>
                </Box>
            </Dialog>

            {/* Error dialog */}
            <ConfirmationDialog
                open={isErrorOpen}
                handleClose={() => setIsErrorOpen(false)}
                onConfirm={() => setIsErrorOpen(false)}
                title="Error"
                message={errorMessage}
                confirmText="Close"
                cancelTextReq={false}
            />
        </Box>
    );
};

export default OnboardLoginResetPasswordScreen;
